import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "preview"]
  static values = {
    url: String,
    maxFileSize: Number,
    maxFiles: Number
  }

  connect() {
    this.setupDropzone()
    this.fileCount = 0
  }

  setupDropzone() {
    // Événements de drag and drop
    this.element.addEventListener("dragover", this.handleDragOver.bind(this))
    this.element.addEventListener("dragleave", this.handleDragLeave.bind(this))
    this.element.addEventListener("drop", this.handleDrop.bind(this))

    // Click pour ouvrir le sélecteur de fichiers
    this.element.addEventListener("click", this.openFileSelector.bind(this))

    // Événement de changement sur l'input file
    if (this.hasInputTarget) {
      this.inputTarget.addEventListener("change", this.handleFileSelect.bind(this))
    }
  }

  // P52 — la zone de dépôt est nuit (.drop-nuit) : le survol d'un fichier
  // pose .is-over, plus de couleurs claires en JS.
  handleDragOver(event) {
    event.preventDefault()
    event.stopPropagation()
    this.element.classList.add("is-over")
  }

  handleDragLeave(event) {
    event.preventDefault()
    event.stopPropagation()
    this.element.classList.remove("is-over")
  }

  handleDrop(event) {
    event.preventDefault()
    event.stopPropagation()
    this.element.classList.remove("is-over")

    const files = event.dataTransfer.files
    this.processFiles(files)
  }

  openFileSelector(event) {
    // Éviter d'ouvrir le sélecteur si on clique sur un bouton ou un lien
    if (event.target.tagName === "BUTTON" || event.target.tagName === "A") {
      return
    }

    if (this.hasInputTarget) {
      this.inputTarget.click()
    }
  }

  handleFileSelect(event) {
    const files = event.target.files
    this.processFiles(files)
  }

  async processFiles(files) {
    const validFiles = this.validateFiles(files)

    if (validFiles.length === 0) {
      return
    }

    // Créer un FormData avec les fichiers
    const formData = new FormData()
    validFiles.forEach(file => {
      formData.append("photos[]", file)
    })

    // Ajouter le token CSRF
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content
    formData.append("authenticity_token", csrfToken)

    // Afficher un indicateur de chargement
    this.showLoadingIndicator()

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token": csrfToken,
          "Accept": "text/vnd.turbo-stream.html"
        }
      })

      if (response.ok) {
        // Turbo Stream va automatiquement mettre à jour le DOM
        const html = await response.text()
        Turbo.renderStreamMessage(html)

        // Optionnel: Afficher un message de succès
        this.showSuccess(`${validFiles.length} photo(s) ajoutée(s)`)
      } else {
        const text = await response.text()
        this.showError("Erreur lors de l'upload des photos")
        console.error("Upload error:", text)
      }
    } catch (error) {
      this.showError("Erreur réseau lors de l'upload")
      console.error("Network error:", error)
    } finally {
      this.hideLoadingIndicator()
    }
  }

  validateFiles(files) {
    const validFiles = []
    const maxSize = this.maxFileSizeValue * 1024 * 1024 // Convertir MB en bytes
    const maxFiles = this.maxFilesValue
    const acceptedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"]

    let errors = []

    // Vérifier si on peut ajouter des fichiers
    if (maxFiles <= 0) {
      errors.push("La limite de photos est atteinte. Supprimez une photo avant d'en ajouter de nouvelles.")
      this.showError(errors.join("\n"))
      return validFiles
    }

    Array.from(files).forEach((file, index) => {
      // Vérifier le nombre max de fichiers
      if (validFiles.length >= maxFiles) {
        errors.push(`Vous pouvez ajouter seulement ${maxFiles} photo(s) supplémentaire(s)`)
        return
      }

      // Vérifier le type de fichier
      if (!acceptedTypes.includes(file.type)) {
        errors.push(`${file.name}: Type de fichier non supporté`)
        return
      }

      // Vérifier la taille
      if (file.size > maxSize) {
        const sizeMB = (file.size / 1024 / 1024).toFixed(1)
        errors.push(`${file.name}: Fichier trop volumineux (${sizeMB}MB)`)
        return
      }

      validFiles.push(file)
    })

    // Afficher les erreurs s'il y en a
    if (errors.length > 0) {
      this.showError(errors.join("\n"))
    }

    return validFiles
  }

  showLoadingIndicator() {
    // Remplacer le contenu de la dropzone par un indicateur de chargement
    const originalContent = this.element.innerHTML
    this.element.dataset.originalContent = originalContent

    this.element.innerHTML = `
      <div class="flex flex-col items-center justify-center h-full">
        <svg class="animate-spin h-12 w-12 text-emerald-300 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <p class="text-slate-300">Upload en cours...</p>
      </div>
    `
  }

  hideLoadingIndicator() {
    // Restaurer le contenu original
    if (this.element.dataset.originalContent) {
      this.element.innerHTML = this.element.dataset.originalContent
      delete this.element.dataset.originalContent

      // Réinitialiser les événements
      this.setupDropzone()
    }
  }

  showSuccess(message) {
    // Créer une alerte de succès temporaire
    const alert = document.createElement("div")
    alert.className = "fixed top-4 right-4 menu-nuit border-emerald-400/40 text-emerald-200 text-sm px-4 py-3 z-50"
    alert.innerHTML = `
      <div class="flex items-center">
        <svg class="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
        </svg>
        <span>${message}</span>
      </div>
    `

    document.body.appendChild(alert)

    // Supprimer l'alerte après 3 secondes
    setTimeout(() => {
      alert.style.transition = "opacity 0.5s"
      alert.style.opacity = "0"
      setTimeout(() => alert.remove(), 500)
    }, 3000)
  }

  showError(message) {
    // Créer une alerte temporaire
    const alert = document.createElement("div")
    alert.className = "fixed top-4 right-4 menu-nuit border-red-400/50 text-red-200 text-sm px-4 py-3 z-50"
    alert.innerHTML = `
      <div class="flex items-center">
        <svg class="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
        </svg>
        <span>${message.replace(/\n/g, "<br>")}</span>
      </div>
    `

    document.body.appendChild(alert)

    // Supprimer l'alerte après 5 secondes
    setTimeout(() => {
      alert.style.transition = "opacity 0.5s"
      alert.style.opacity = "0"
      setTimeout(() => alert.remove(), 500)
    }, 5000)
  }
}